import { ADDRESS_ID, ASSET_ID } from 'scenes/common/api/quote/marshaller/ids';
import { HomeFunnelState } from 'scenes/home/flows/types';
import {
  anyFutureRenovationPlanned,
  areHazardGoodsKeptAtHome,
  getMortgageProvider,
  getMortgageProviderOther,
  getNumberOfVisits,
  getPropertyBuiltYear,
  getPropertyBusinessType,
  getPropertyInGoodCondition,
  getPurposeOfProperty,
  getSecurityFeatures,
  hasAdditionalSecurityInstalled,
  hasBeenRewiredOrReplumbed,
  hasContentsCoverType,
  hasMoreThan5UnrelatedPeople,
  hasSpecifiedContents,
  heritageListedAnswerRequired,
  isCashKeptAtHome,
  isHeritageListed,
  isOver5Acres,
  isPropertyInGoodCondition,
  isPropertyOwned,
  isPropertyUnderRenovation,
  isPropertyUnoccupied,
  isPropertyUsedForBusiness,
  isStockKeptAtHome,
  rewiredOrReplumbedAnswerRequired,
} from 'scenes/home/selectors';
import { PropertyPurposeType } from 'scenes/home/types';
import {
  WireAsset,
  WireBusinessSpecific,
  WirePurpose,
} from 'scenes/common/api/quote/types';
import {
  toWireMortgageLender,
  toWireSecurities,
} from 'scenes/common/api/quote/marshaller/assets';

import { toWireBuilding } from './building';
import { coverageRefIds } from './coverage';
import { partyRefIds } from './party';
import { toWireBuildingCoverSI } from './buildingCoverSI';
import { WireNumberOfVisits } from '../types/assets.types';

const wirePurpose: { [key in PropertyPurposeType]: WirePurpose } = {
  [PropertyPurposeType.HolidaysHouse]: WirePurpose.HldHmIncm,
  [PropertyPurposeType.Investment]: WirePurpose.OwnNRented,
  [PropertyPurposeType.PPR]: WirePurpose.OwnNLive,
  [PropertyPurposeType.Unspecified]: WirePurpose.Unspecified,
};

const toWireNumberOfVisits = (numberOfVisits: number): WireNumberOfVisits => {
  switch (numberOfVisits) {
    case 0:
      return WireNumberOfVisits.None;
    case 1:
      return WireNumberOfVisits.OneToFour;
    case 5:
      return WireNumberOfVisits.FiveOrMore;
    default:
      return WireNumberOfVisits.Unspecified;
  }
};

const toWireBusinessSpecific = (
  state: HomeFunnelState
): WireBusinessSpecific => {
  const usedForBusiness = isPropertyUsedForBusiness(state);

  return {
    toStoreCash: usedForBusiness ? isCashKeptAtHome(state) : false,
    toStoreStock: usedForBusiness ? isStockKeptAtHome(state) : false,
    toStoreHazardGoods: usedForBusiness
      ? areHazardGoodsKeptAtHome(state)
      : false,
    numberOfVisits: usedForBusiness
      ? toWireNumberOfVisits(getNumberOfVisits(state))
      : '',
    businessType: usedForBusiness ? getPropertyBusinessType(state) : '',
  };
};

export const toWireAssets = (state: HomeFunnelState): WireAsset[] => [
  {
    addressRefId: ADDRESS_ID,
    coverageRefIds: coverageRefIds(state),
    hasContentsItems: hasSpecifiedContents(state),
    hasAddSecurity: hasAdditionalSecurityInstalled(state),
    id: ASSET_ID,
    partyRefIds: partyRefIds(state),
    isOwned: isPropertyOwned(state),
    purpose: wirePurpose[getPurposeOfProperty(state)],
    securities: hasContentsCoverType(state)
      ? toWireSecurities(getSecurityFeatures(state))
      : undefined,
    isUsedForBusiness: isPropertyUsedForBusiness(state),
    businessSpecific: toWireBusinessSpecific(state),
    building: toWireBuilding(state),
    buildingCoverSI: toWireBuildingCoverSI(state),
    isHeritageListed:
      heritageListedAnswerRequired(getPropertyBuiltYear(state)) &&
      isHeritageListed(state),
    isRewiredOrReplumbed:
      rewiredOrReplumbedAnswerRequired(getPropertyBuiltYear(state)) &&
      hasBeenRewiredOrReplumbed(state),
    isMaintained: getPropertyInGoodCondition(state)
      ? isPropertyInGoodCondition(state)
      : undefined,
    hasMaxUnrelatedOccupants: hasMoreThan5UnrelatedPeople(state),
    isUnderConstruction: isPropertyUnderRenovation(state),
    hasPlannedConstruction:
      !isPropertyUnderRenovation(state) && anyFutureRenovationPlanned(state),
    isOver5Acres: isOver5Acres(state),
    isUnoccupied: isPropertyUnoccupied(state),
    mortgageLender: toWireMortgageLender(
      getMortgageProvider(state),
      getMortgageProviderOther(state)
    ),
  },
];

export const assetRefIds = (state: HomeFunnelState): string[] => [
  ...toWireAssets(state).map((asset) => asset.id),
];
